import { ActionIcon, Box, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconUsersGroup } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Id } from '@feature/utils';

import { getTravelRequestOverlapsOptions } from '../model/TravelRequestQuery';
import { TravelOverlapDrawer } from './TravelOverlapDrawer';

interface TravelOverlapButtonProps {
  requestId: Id;
  overlapCount: number;
}

interface TravelOverlapCardFrameProps extends TravelOverlapButtonProps {
  children: ReactNode;
}

export function TravelOverlapButton({ requestId, overlapCount }: TravelOverlapButtonProps) {
  const { t } = useTranslation();
  const [opened, { open, close }] = useDisclosure(false);
  const { data } = useQuery({ ...getTravelRequestOverlapsOptions(requestId), enabled: opened });
  const label = t('travel.overlaps.iconLabel', { count: opened && data != null ? data.length : overlapCount });

  return (
    <>
      <Tooltip label={label}>
        <ActionIcon
          variant="filled"
          color="yellow"
          style={{ boxShadow: '0 0 0 2px var(--mantine-color-body)' }}
          size="sm"
          radius="xl"
          aria-label={label}
          onClick={open}
        >
          <IconUsersGroup size={14} />
        </ActionIcon>
      </Tooltip>
      <TravelOverlapDrawer
        requestId={requestId}
        opened={opened}
        onClose={close}
      />
    </>
  );
}

// Sits on the card's corner, within its padding, because the card is a link that cannot hold a button.
export function TravelOverlapCardFrame({ requestId, overlapCount, children }: TravelOverlapCardFrameProps) {
  return (
    <Box pos="relative">
      {children}
      {overlapCount > 0 && (
        <Box
          pos="absolute"
          top={-6}
          right={-6}
        >
          <TravelOverlapButton
            requestId={requestId}
            overlapCount={overlapCount}
          />
        </Box>
      )}
    </Box>
  );
}
